import React, { useEffect, useState } from 'react';

import { CtLogo } from '@/components/brand/index.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import {
  AccessDeniedError,
  isAccessDenied,
  signInWithEmail,
  signInWithMicrosoft,
  signInWithTeamsSso,
  ssoConfigured,
} from '@/lib/sso.js';
import { isRunningInTeams } from '@/lib/teams-env.js';
import { logger } from '@/logger.js';
import { getState, subscribe } from '@/store/identity-store.js';

import { AccessDeniedScreen } from './AccessDeniedScreen.js';

interface Props {
  children: React.ReactNode;
}

/**
 * Authorizes the user before the app loads.
 *
 *  - **Teams Tab** — non-interactive: exchanges the tab's Teams SSO token for
 *    a platform JWT on boot (`signInWithTeamsSso`). A splash shows while the
 *    exchange runs; on failure an error screen with Retry appears. The app
 *    never mounts without a JWT, so there is no unauthenticated 401 storm.
 *  - **Standalone browser** — requires a platform JWT. Until one is present we
 *    show the login screen, which obtains it from cti-identity-service via the
 *    gateway (`POST /v1/auth/token`).
 *
 * Either way the JWT is attached as `Bearer` on every `/v1` call and persisted
 * so a refresh re-resolves identity from `/v1/me`.
 *
 * A third outcome sits beside those two: the exchange can answer 403, meaning
 * the access gate refused this account. That is a verdict, not a hiccup, so the
 * gate swaps in the denial screen rather than an error with a Retry button —
 * and both hosts land there, because in a Teams tab nothing else would be shown
 * at all.
 */
export default function AuthGate({ children }: Props): React.ReactElement {
  const [jwt, setJwt] = useState<string | null>(() => getState().platformJwt);
  // Snapshot once — the host can't change within a tab's lifetime.
  const [inTeams] = useState(isRunningInTeams);
  const [teamsError, setTeamsError] = useState<string | null>(null);
  // Seeded from the renewal core: a remount must not forget a denial already
  // recorded in this page's life.
  const [accessDenied, setAccessDenied] = useState(isAccessDenied);
  const [attempt, setAttempt] = useState(0);

  useEffect(
    () =>
      subscribe((s) => {
        setJwt(s.platformJwt);
        // A revoke mid-session is refused during renewal, deep inside a stream or
        // a request — not in the sign-in path this gate drives. That refusal
        // latches in the renewal core and the session is cleared, which lands
        // here as "no JWT". Reading the latch is what shows the denial instead of
        // the login screen, which would otherwise ask the user to sign in again
        // only to be told no.
        if (s.platformJwt === null && isAccessDenied()) setAccessDenied(true);
      }),
    [],
  );

  // Teams Tab bootstrap. signInWithTeamsSso() is single-flight, so the
  // StrictMode double-effect shares one SDK round-trip + exchange. Success
  // lands in the identity store; the subscription above flips `jwt` and the
  // gate re-renders into the app — nothing to do here but record failures.
  //
  // A denial keeps it from running at all: a revoke mid-session clears the JWT,
  // which otherwise reads as "no session yet, bootstrap again" and spends two
  // Teams SDK calls (each with an 8-second timeout) to arrive at the refusal
  // already on screen.
  useEffect(() => {
    if (!inTeams || jwt || accessDenied) return;
    let cancelled = false;
    setTeamsError(null);
    signInWithTeamsSso().catch((err: unknown) => {
      logger.warn('AuthGate: Teams SSO sign-in failed', err);
      if (cancelled) return;
      if (err instanceof AccessDeniedError) {
        setAccessDenied(true);
        return;
      }
      setTeamsError(err instanceof Error ? err.message : String(err));
    });
    return () => {
      cancelled = true;
    };
  }, [inTeams, jwt, attempt, accessDenied]);

  if (accessDenied) {
    return <AccessDeniedScreen />;
  }
  if (jwt) {
    return <>{children}</>;
  }
  if (inTeams) {
    if (teamsError !== null) {
      return (
        <TeamsSignInError
          detail={teamsError}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      );
    }
    return (
      <AuthShell>
        <p className="text-sm text-muted-foreground" role="status">
          Signing you in…
        </p>
      </AuthShell>
    );
  }
  return <LoginScreen onAccessDenied={() => setAccessDenied(true)} />;
}

/** Shared full-screen frame for every pre-auth state (login, splash, error). */
function AuthShell({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden bg-background px-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-8 text-center">
        <div className="flex items-center gap-3.5">
          <CtLogo
            variant="mark"
            tone="mono"
            className="size-11 text-[#18399b] [[data-theme='dark']_&]:text-white"
            aria-label="Customertimes"
          />
          <h1 className="text-2xl font-bold leading-[1.1] tracking-[-0.01em] text-foreground">
            Agent
          </h1>
        </div>
        {children}
      </div>
    </div>
  );
}

const PRIMARY_BUTTON_CLASS =
  'inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground transition-all hover:shadow-lg active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60';

function TeamsSignInError({
  detail,
  onRetry,
}: {
  detail: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <AuthShell>
      <div className="space-y-3">
        <p className="font-medium text-foreground">Couldn’t sign you in.</p>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Teams single sign-on didn’t complete. This usually means the tenant
          admin hasn’t consented to the{' '}
          <code className="font-mono">access_as_user</code> scope for this app
          yet, or the platform’s Entra token exchange isn’t configured.
        </p>
        <p className="text-[11px] text-muted-foreground/70">
          Details: <code className="font-mono">{detail}</code>
        </p>
      </div>
      <button type="button" onClick={onRetry} className={PRIMARY_BUTTON_CLASS}>
        Try again
      </button>
    </AuthShell>
  );
}

/** `onAccessDenied` hands a 403 from the exchange up to the gate, which owns the denial screen. */
function LoginScreen({
  onAccessDenied,
}: {
  onAccessDenied: () => void;
}): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState(
    () =>
      (import.meta.env['VITE_DEV_SSO_EMAIL'] as string | undefined)?.trim() ??
      '',
  );
  const ssoEnabled = ssoConfigured();

  async function signIn(): Promise<void> {
    if (loading || (!ssoEnabled && !email.trim())) return;
    setLoading(true);
    setError(null);
    try {
      if (ssoEnabled) await signInWithMicrosoft();
      else await signInWithEmail(email.trim());
    } catch (err: unknown) {
      if (err instanceof AccessDeniedError) {
        onAccessDenied();
        return;
      }
      setError('Sign-in failed. Try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell>
      <form
        className="w-full space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void signIn();
        }}
      >
        {!ssoEnabled && (
          <div className="space-y-2 text-left">
            <p className="text-sm text-muted-foreground">Development sign-in</p>
            <Label htmlFor="sign-in-email">Email</Label>
            <Input
              id="sign-in-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              disabled={loading}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
        )}
        <button
          type="submit"
          disabled={loading || (!ssoEnabled && !email.trim())}
          className={PRIMARY_BUTTON_CLASS}
        >
          {loading
            ? 'Signing in…'
            : ssoEnabled
              ? 'Sign in with Microsoft'
              : 'Sign in'}
        </button>
      </form>
      {error !== null && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </AuthShell>
  );
}
